/**
 * @fileoverview Above-the-fold hero — the first thing a visitor sees.
 *
 * Two-column layout on desktop: pitch copy + CTA on the left, real
 * admin-dashboard screenshot inside a browser frame on the right with
 * two floating "live match" / "won by 7 wkts" chips for warmth. The
 * `Metric` sub-component renders the 4s / 6s / Dots stat cells inside
 * the won-chip and lives here because it's only used by this hero.
 *
 * Extracted verbatim out of the original Landing/index.tsx.
 */
import { Link } from 'react-router-dom'
import { Download, LogIn, Sparkles, Trophy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { APK_DOWNLOAD_PATH, HERO_IMAGE } from '../constants'

export function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Soft brand-color wash so the section reads as a hero, not a body
          chunk, without committing to a full-bleed image background. */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-primary/8 via-background to-background" />
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div className="flex flex-col justify-center">
          <Badge variant="secondary" className="mb-5 w-fit gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            Built for amateur and professional cricket
          </Badge>
          <h1 className="text-balance text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Run cricket tournaments end-to-end —{' '}
            <span className="bg-gradient-to-r from-primary to-orange-600 bg-clip-text text-transparent">
              from fixture to final
            </span>
            .
          </h1>
          <p className="mt-6 max-w-xl text-lg text-muted-foreground">
            Future Protea is a complete platform for scheduling matches,
            scoring ball-by-ball, computing standings, and pushing live
            updates to every player, umpire and fan on the field.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="gap-2">
              <a href={APK_DOWNLOAD_PATH} download>
                <Download className="h-4 w-4" />
                Download Android app
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="gap-2">
              <Link to="/login">
                <LogIn className="h-4 w-4" />
                Admin login
              </Link>
            </Button>
          </div>

          <p className="mt-4 text-sm text-muted-foreground">
            Latest release: <span className="font-medium text-foreground">v1.3.1</span> · ~73 MB ·
            Android 8+
          </p>
        </div>

        <HeroVisual />
      </div>
    </section>
  )
}

function HeroVisual() {
  return (
    <div className="relative mx-auto w-full max-w-2xl lg:max-w-none">
      {/* Brand-color halo behind the browser frame for warmth. */}
      <div className="absolute inset-0 -z-10 scale-110 bg-[radial-gradient(circle_at_center,oklch(0.70_0.18_45/.30),transparent_70%)] blur-3xl" />

      {/* Browser-style frame around the real admin dashboard screenshot
          so the visual instantly communicates "this is the actual product". */}
      <div className="overflow-hidden rounded-2xl border border-foreground/10 bg-background shadow-2xl ring-1 ring-black/5">
        <div className="flex items-center gap-2 border-b bg-muted/60 px-3 py-2">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </div>
          <div className="mx-auto flex items-center gap-1.5 rounded-md bg-background/80 px-3 py-0.5 text-[11px] text-muted-foreground">
            <span className="text-emerald-600">●</span>
            futureprotea.com/dashboard
          </div>
        </div>
        <img
          src={HERO_IMAGE}
          alt="Future Protea admin dashboard"
          className="block h-auto w-full"
          loading="eager"
        />
      </div>

      {/* Floating LIVE chip — top right, gentle tilt for energy. */}
      <Card className="absolute -right-3 -top-4 w-44 rotate-3 overflow-hidden border-primary/30 bg-background/95 shadow-xl backdrop-blur sm:-right-6 sm:-top-6">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary/[0.08] via-primary/[0.02] to-transparent"
        />
        <CardContent className="relative p-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Live · CCL
            </span>
            <Badge variant="destructive" className="gap-1 px-1.5 py-0 text-[9px]">
              <span className="h-1 w-1 animate-pulse rounded-full bg-white" />
              LIVE
            </Badge>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold">RR</span>
            <span className="text-lg font-bold tabular-nums">164/3</span>
          </div>
          <p className="text-[10px] text-muted-foreground">18.1 ov · CRR 9.03</p>
        </CardContent>
      </Card>

      {/* Floating result chip — bottom left, opposite tilt. */}
      <Card className="absolute -bottom-5 -left-3 w-48 -rotate-2 overflow-hidden border-emerald-500/40 bg-background/95 shadow-xl backdrop-blur sm:-bottom-7 sm:-left-6">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-emerald-500/[0.10] via-emerald-500/[0.03] to-transparent"
        />
        <CardContent className="relative p-3">
          <div className="mb-1 flex items-center gap-1.5">
            <Trophy className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600">
              Won by 7 wkts
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Metric label="4s" value="14" />
            <Metric label="6s" value="6" />
            <Metric label="Dots" value="22" />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-base font-bold tabular-nums leading-tight">{value}</p>
      <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  )
}
